import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import debounce from 'lodash/debounce';
// Api
import { GET_MENTIONS_STORES } from 'api/store/queries';
import { StoreStatus, StoresOrderBy, UserRole } from 'api/graphql-global-types';
import {
  GetMentionsStores,
  GetMentionsStoresVariables,
} from 'api/store/types/GetMentionsStores';
// Ui
import Input from 'ui3/Input/Input';
import Loader from 'ui3/Loader/Loader';
// Styles
import styles from './CompetitorSearchInput.module.scss';

export type CompetitorSearchResult = {
  storeId: string;
  name: string;
  avatarURL?: string;
};

type CompetitorSearchInputProps = {
  onSelect: (competitor: CompetitorSearchResult) => void;
  excludeStoreIds?: string[];
  initialSearchTerm?: string;
  onSearchTermChange?: (searchTerm: string) => void;
  /** Shown once the host leaves the field with text that wasn't picked from the results. */
  unpickedError?: string;
  'aria-label'?: string;
};

const STORE_ROLES = [
  UserRole.Athlete,
  UserRole.Organization,
  UserRole.ContentCreator,
];

const CompetitorSearchInput = ({
  onSelect,
  excludeStoreIds = [],
  initialSearchTerm = '',
  onSearchTermChange,
  unpickedError,
  'aria-label': ariaLabel,
}: CompetitorSearchInputProps): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [showDropdown, setShowDropdown] = useState(false);
  // A restored term was already left unpicked once, so it is flagged straight away.
  const [hasLeftField, setHasLeftField] = useState(
    Boolean(initialSearchTerm.trim())
  );
  const rootRef = useRef<HTMLDivElement>(null);

  const { data, loading, refetch } = useQuery<
    GetMentionsStores,
    GetMentionsStoresVariables
  >(GET_MENTIONS_STORES, {
    variables: {
      storeRoles: STORE_ROLES,
      input: { limit: 20, offset: 0 },
    },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!showDropdown) return undefined;

    const handleOutsideClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showDropdown]);

  const results = (data?.stores?.entities || [])
    .map((entity) => ({
      storeId: entity?.storeDetails?.storeId,
      name: entity?.storeDetails?.storeName || '',
      avatarURL: entity?.storeDetails?.avatarURL || undefined,
    }))
    .filter(
      (result) => result.storeId && !excludeStoreIds.includes(result.storeId)
    ) as CompetitorSearchResult[];

  const handleSearch = useMemo(() => {
    const loadOptions = (storeName: string) => {
      if (storeName.trim().length > 0) {
        refetch({
          storeRoles: STORE_ROLES,
          input: {
            storeName,
            orderBy: StoresOrderBy.storeName,
            status: [StoreStatus.Active],
            limit: 20,
          },
        });
        setShowDropdown(true);
      } else {
        setShowDropdown(false);
      }
    };

    return debounce(loadOptions, 400);
  }, [refetch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setHasLeftField(false);
    onSearchTermChange?.(value);
    handleSearch(value);
  };

  const handleBlur = () => {
    setHasLeftField(true);
  };

  // Keeps focus in the input so its blur hint can't push the list down mid-click.
  const handleOptionMouseDown = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleSelect = (result: CompetitorSearchResult) => {
    onSelect(result);
    setSearchTerm('');
    setShowDropdown(false);
  };

  return (
    <div className={styles.root} ref={rootRef}>
      <Input
        name="competitorSearch"
        aria-label={ariaLabel}
        placeholder="Search on MILLIONS"
        icon="search"
        value={searchTerm}
        error={hasLeftField && searchTerm.trim() ? unpickedError : undefined}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      {showDropdown && (
        <div className={styles.dropdown}>
          {loading && (
            <div className={styles.emptyState}>
              <Loader loading size="small" />
            </div>
          )}
          {!loading &&
            results.length > 0 &&
            results.map((result) => (
              <button
                key={result.storeId}
                type="button"
                className={styles.option}
                onMouseDown={handleOptionMouseDown}
                onClick={() => handleSelect(result)}
              >
                <img
                  src={result.avatarURL || '/favicon.png'}
                  alt=""
                  className={styles.avatar}
                />
                <span>{result.name}</span>
              </button>
            ))}
          {!loading && results.length === 0 && (
            <div className={styles.emptyState}>No matches found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompetitorSearchInput;
